// ============================================================
//  src/admin/pages/AdminPlansPage.js
// ============================================================
import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { adminListPlans, adminCreatePlan, adminUpdatePlan, adminDeletePlan } from '../api/adminClient';
import { PageHeader, Badge, isFlagTrue } from '../components/UI';
import DataTable from '../components/DataTable';
import { useConfirm } from '../components/ConfirmModal';

const FEATURE_FLAGS = [
  { key: 'plAllowBroadcast',     label: 'Broadcast / Bulk Message',    desc: 'Send a message to all bot users at once', icon: '📢' },
  { key: 'plAllowSingleMessage', label: 'Single Direct Message',       desc: 'Send a message to one specific user from the Users tab', icon: '💬' },
  { key: 'plAllowScheduled',     label: 'Scheduled Messages',          desc: 'Schedule messages to be sent at a future date/time', icon: '🕐' },
  { key: 'plAllowCollaboration', label: 'Collaboration / Team Access', desc: 'Invite team members to manage bots together', icon: '👥' },
  { key: 'plAllowCommunity',     label: 'Community Forum',             desc: 'Access to post, reply and vote in the community', icon: '🌐' },
  { key: 'plAllowAiIntegration', label: 'AI Integration',              desc: 'Connect OpenAI, Claude or Gemini to power bot replies', icon: '🤖' },
];

const EMPTY_FORM = {
  plId: null, plName: '', plSlug: '', plPrice: 0, plInterval: '',
  plDescription: '', plFeatures: '', plMaxBots: '', plMaxMessages: '',
  plIsActive: true, plIsPublic: true, plSortOrder: 0,
  plAllowBroadcast: false, plAllowSingleMessage: false, plAllowScheduled: false,
  plAllowCollaboration: false, plAllowCommunity: false, plAllowAiIntegration: false,
};

function fmtNaira(n) {
  return '₦' + (Number(n) || 0).toLocaleString('en-NG', { maximumFractionDigits: 0 });
}

export default function AdminPlansPage() {
  const confirm = useConfirm();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    adminListPlans()
      .then(res => setPlans(res.data.plans))
      .catch(err => toast.error(err?.response?.data?.message || 'Could not load plans'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  function openCreate() {
    setForm(EMPTY_FORM);
    setShowForm(true);
  }

  function openEdit(plan) {
    setForm({
      plId: plan.plId,
      plName: plan.plName,
      plSlug: plan.plSlug,
      plPrice: plan.plPrice,
      plInterval: plan.plInterval,
      plDescription: plan.plDescription || '',
      plFeatures: (plan.plFeatures || []).join('\n'),
      plMaxBots: plan.plMaxBots ?? '',
      plMaxMessages: plan.plMaxMessages ?? '',
      plIsActive: isFlagTrue(plan.plIsActive),
      plIsPublic: isFlagTrue(plan.plIsPublic),
      plSortOrder: plan.plSortOrder,
      plAllowBroadcast: isFlagTrue(plan.plAllowBroadcast),
      plAllowSingleMessage: isFlagTrue(plan.plAllowSingleMessage),
      plAllowScheduled: isFlagTrue(plan.plAllowScheduled),
      plAllowCollaboration: isFlagTrue(plan.plAllowCollaboration),
      plAllowCommunity: isFlagTrue(plan.plAllowCommunity),
      plAllowAiIntegration: isFlagTrue(plan.plAllowAiIntegration),
    });
    setShowForm(true);
  }

  const setFlag = (key, val) => setForm(f => ({ ...f, [key]: val }));

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      plFeatures: form.plFeatures.split('\n').map(s => s.trim()).filter(Boolean),
      plMaxBots: form.plMaxBots === '' ? null : Number(form.plMaxBots),
      plMaxMessages: form.plMaxMessages === '' ? null : Number(form.plMaxMessages),
    };
    try {
      if (form.plId) {
        await adminUpdatePlan(payload);
        toast.success('Plan updated');
      } else {
        await adminCreatePlan(payload);
        toast.success('Plan created');
      }
      setShowForm(false);
      load();
    } catch (e2) {
      toast.error(e2?.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(plId) {
    const ok = await confirm({
      title: 'Disable this plan?',
      message: 'Existing subscribers stay on it, but it will no longer be shown to new users.',
      confirmText: 'Disable Plan',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await adminDeletePlan(plId);
      toast.success('Plan disabled');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Delete failed');
    }
  }

  const columns = [
    { key: 'name', label: 'Name', primary: true, render: p => <strong>{p.plName}</strong> },
    { key: 'slug', label: 'Slug', render: p => <code className="bf-mono">{p.plSlug}</code> },
    {
      key: 'price', label: 'Price',
      render: p => p.plPrice > 0 ? `${fmtNaira(p.plPrice)} / ${p.plInterval ? `${p.plInterval}d` : '∞'}` : 'Free',
    },
    {
      key: 'features', label: 'Features',
      render: p => (
        <div className="bf-flex-wrap" style={{ gap: 4 }}>
          {FEATURE_FLAGS.map(({ key, icon, label }) =>
            isFlagTrue(p[key]) ? <Badge key={key} tone="primary">{icon} {label}</Badge> : null
          )}
          {FEATURE_FLAGS.every(({ key }) => !isFlagTrue(p[key])) && <span className="bf-muted" style={{ fontSize: 11 }}>No features</span>}
        </div>
      ),
    },
    {
      key: 'status', label: 'Status',
      render: p => isFlagTrue(p.plIsActive)
        ? <Badge tone="success">{isFlagTrue(p.plIsPublic) ? 'Active' : 'Active (hidden)'}</Badge>
        : <Badge tone="muted">Disabled</Badge>,
    },
    {
      key: 'actions', label: '', align: 'right',
      render: p => (
        <div style={{ whiteSpace: 'nowrap' }}>
          <button className="btn btn-outline bf-btn-sm" style={{ marginRight: 6 }} onClick={() => openEdit(p)}>Edit</button>
          {isFlagTrue(p.plIsActive) && (
            <button className="bf-btn-danger-outline bf-btn-sm" onClick={() => handleDelete(p.plId)}>Disable</button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="bf-page">
      <PageHeader
        title="Plans"
        subtitle="Manage pricing tiers and feature access."
        actions={<button className="btn btn-primary" onClick={openCreate}>+ New Plan</button>}
      />

      {/* Form */}
      {showForm && (
        <div className="bf-card">
          <div className="bf-card-hdr">{form.plId ? 'Edit Plan' : 'New Plan'}</div>
          <form onSubmit={handleSave} className="bf-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            <div className="bf-form-grid">
              <div className="bf-field">
                <label className="bf-field-label">Name</label>
                <input className="bf-input" required value={form.plName} onChange={e => setForm({ ...form, plName: e.target.value })} />
              </div>
              <div className="bf-field">
                <label className="bf-field-label">Slug</label>
                <input className="bf-input" required value={form.plSlug} onChange={e => setForm({ ...form, plSlug: e.target.value })} />
              </div>
              <div className="bf-field">
                <label className="bf-field-label">Price (₦)</label>
                <input className="bf-input" type="number" min="0" value={form.plPrice} onChange={e => setForm({ ...form, plPrice: e.target.value })} />
              </div>
              <div className="bf-field">
                <label className="bf-field-label">Duration (days, blank = never expires)</label>
                <input className="bf-input" type="number" min="1" placeholder="e.g. 30 for monthly, 365 for yearly"
                  value={form.plInterval} onChange={e => setForm({ ...form, plInterval: e.target.value })} />
              </div>
              <div className="bf-field">
                <label className="bf-field-label">Max Bots (blank = unlimited)</label>
                <input className="bf-input" type="number" min="0" value={form.plMaxBots} onChange={e => setForm({ ...form, plMaxBots: e.target.value })} />
              </div>
              <div className="bf-field">
                <label className="bf-field-label">Max Messages (blank = unlimited)</label>
                <input className="bf-input" type="number" min="0" value={form.plMaxMessages} onChange={e => setForm({ ...form, plMaxMessages: e.target.value })} />
              </div>
              <div className="bf-field">
                <label className="bf-field-label">Sort Order</label>
                <input className="bf-input" type="number" value={form.plSortOrder} onChange={e => setForm({ ...form, plSortOrder: e.target.value })} />
              </div>
            </div>

            <div className="bf-field">
              <label className="bf-field-label">Description</label>
              <textarea className="bf-input" style={{ minHeight: 64 }} value={form.plDescription}
                onChange={e => setForm({ ...form, plDescription: e.target.value })} />
            </div>

            <div className="bf-field">
              <label className="bf-field-label">Features (one per line — shown as bullet points on billing page)</label>
              <textarea className="bf-input" style={{ minHeight: 90 }} value={form.plFeatures}
                onChange={e => setForm({ ...form, plFeatures: e.target.value })} />
            </div>

            {/* Feature Access Toggles */}
            <div>
              <div className="bf-field-label" style={{ marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--bf-text-muted)' }}>
                Feature Access
              </div>
              <div className="bf-toggle-grid">
                {FEATURE_FLAGS.map(({ key, label, desc, icon }) => {
                  const enabled = !!form[key];
                  return (
                    <div
                      key={key}
                      onClick={() => setFlag(key, !enabled)}
                      className={`bf-toggle-card${enabled ? ' bf-on' : ''}`}
                    >
                      <div className={`bf-toggle-pill${enabled ? ' bf-on' : ''}`}>
                        <div className={`bf-toggle-knob${enabled ? ' bf-on' : ''}`} />
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div className="bf-toggle-label">{icon} {label}</div>
                        <div className="bf-toggle-desc">{desc}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Visibility toggles */}
            <div className="bf-checkbox-row">
              <label className="bf-checkbox">
                <input type="checkbox" checked={form.plIsActive} onChange={e => setForm({ ...form, plIsActive: e.target.checked })} />
                Active
              </label>
              <label className="bf-checkbox">
                <input type="checkbox" checked={form.plIsPublic} onChange={e => setForm({ ...form, plIsPublic: e.target.checked })} />
                Public (shown on billing page)
              </label>
            </div>

            <div className="bf-btn-row">
              <button className="btn btn-primary" type="submit" disabled={saving}>
                {saving ? 'Saving…' : 'Save Plan'}
              </button>
              <button className="btn btn-outline" type="button" onClick={() => setShowForm(false)}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Plans table */}
      <div className="bf-card">
        <div className="bf-card-body-flush">
          <DataTable
            columns={columns}
            rows={plans}
            keyField="plId"
            loading={loading}
            loadingMessage="Loading plans…"
            emptyMessage="No plans yet — create your first one."
          />
        </div>
      </div>
    </div>
  );
}